"use client";

import { Placeholder, Text } from "@lmn/design-system";
import styled from "styled-components";
import media from "@/styles/mediaQueries";

const Container = styled.div`
  margin: 0;
  border: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.div<{ $isActive?: boolean }>`
  flex: 1;
  padding: 1rem 1.5rem;
  background: transparent;
  position: relative;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.kallisto.font.family.sans};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.medium};
  font-size: ${({ theme }) => theme.kallisto.font.size[200]};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight[300]};
  text-align: center;
  color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.kallisto.color.text.interactive.textPrimaryDefault
      : theme.kallisto.color.text.static.textPrimary};

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
    transform: scaleX(${({ $isActive }) => ($isActive ? 1 : 0)});
  }
`;

const TabContent = styled.div`
  padding: 1.5rem 0;
`;

const ContentWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  padding: "0 16px",
  ...media.screenMdMin({
    flexDirection: "row",
    gap: "24px",
    width: "100%",
    padding: "0 24px",
  }),
}));

const FacilityWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flex: 1,
}));

const FacilitiesContainer = styled.div(() => ({
  display: "flex",
  gap: "8px",
  overflowX: "auto",
  scrollSnapType: "x mandatory",
  WebkitOverflowScrolling: "touch",
  paddingBottom: "8px",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  ...media.screenMdMin({
    flexDirection: "column",
    overflowX: "visible",
    scrollSnapType: "none",
    paddingBottom: 0,
  }),
}));

const FacilitiesRow = styled.div(() => ({
  display: "contents",
  ...media.screenMdMin({
    display: "flex",
    gap: "8px",
  }),
}));

const FacilityCardPlaceholder = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  border: `1px solid ${theme.kallisto.color.border.static.borderDisabled}`,
  borderRadius: theme.kallisto.border.radius.radius100,
  gap: "8px",
  minHeight: "80px",
  minWidth: "80px",
  flexShrink: 0,
  scrollSnapAlign: "start",
  ...media.screenMdMin({
    flex: 1,
    minWidth: "auto",
    padding: "8px",
  }),
}));

const SurroundingsWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  position: "relative",
  ...media.screenMdMin({
    width: "324px",
    flexShrink: 0,
  }),
}));

/* Mobile skeleton */
const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space150};
  margin-bottom: ${({ theme }) => theme.kallisto.space.space150};
  ${media.screenMdMin`
    display: none;
  `}
`;

const MobileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

const TABS = ["Überblick", "Hotel-Info", "Ausstattung", "Bewertungen"];

export default function HotelInfoSkeleton() {
  return (
    <>
      {/* Desktop skeleton */}
      <Container className="hidden-on-sm">
        <TabsContainer>
          {TABS.map((tab, index) => (
            <Tab key={tab} $isActive={index === 0}>
              {tab}
            </Tab>
          ))}
        </TabsContainer>
        <TabContent>
          <ContentWrapper>
            <FacilityWrapper>
              <Text variant="Title5">Hauptausstattung</Text>
              <FacilitiesContainer>
                <FacilitiesRow>
                  {[...Array(5)].map((_, i) => (
                    <FacilityCardPlaceholder key={`row1-${i}`}>
                      <Placeholder width="24px" height="24px" />
                      <Placeholder width="60px" height="16px" />
                    </FacilityCardPlaceholder>
                  ))}
                </FacilitiesRow>
                <FacilitiesRow>
                  {[...Array(5)].map((_, i) => (
                    <FacilityCardPlaceholder key={`row2-${i}`}>
                      <Placeholder width="24px" height="24px" />
                      <Placeholder width="60px" height="16px" />
                    </FacilityCardPlaceholder>
                  ))}
                </FacilitiesRow>
              </FacilitiesContainer>
              <Placeholder width="180px" height="16px" />
            </FacilityWrapper>
            <SurroundingsWrapper>
              <Text variant="Title5">In der Umgebung</Text>
              <Placeholder width="100%" height="168px" />
            </SurroundingsWrapper>
          </ContentWrapper>
        </TabContent>
      </Container>

      {/* Mobile skeleton */}
      <MobileContainer>
        <FacilitiesContainer>
          {[...Array(5)].map((_, i) => (
            <FacilityCardPlaceholder key={`mobile-${i}`}>
              <Placeholder width="24px" height="24px" />
              <Placeholder width="60px" height="16px" />
            </FacilityCardPlaceholder>
          ))}
        </FacilitiesContainer>

        <MobileSection>
          <Text variant="Title5">Hotel Info</Text>
          <Placeholder width="100%" height="16px" />
          <Placeholder width="100%" height="16px" />
          <Placeholder width="60%" height="16px" />
          <Placeholder width="100px" height="16px" />
        </MobileSection>
      </MobileContainer>
    </>
  );
}
